import * as z from 'zod';

import * as api from '@gitbook/api-client';
import { MemberRole, type MemberRoleOrGuest } from '@gitbook/api-client';
import { ContentReferenceSchema } from '@gitbook/app-content-refs';
import { arrayOfAll } from '@gitbook/util-ts';

import type { Collection } from './collections';
import type { Space } from './spaces';

export const ContentGoogleAnalyticsConfigSchema = z.object({
    enabled: z.boolean().optional(),
    trackingID: z.string().optional(),
    trackingPrivateViews: z.boolean().optional(),
});
export type ContentGoogleAnalyticsConfig = z.infer<typeof ContentGoogleAnalyticsConfigSchema>;

/*
 * Common settings applying to both spaces and contents.
 */
export const ContentOwnershipSchema = z.object({
    organization: z.string(),
    collection: z.string().optional(),
});
export type ContentOwnership = z.infer<typeof ContentOwnershipSchema>;

const ContentThemeColorsSchema = z.object({
    /**
     * Primary color for text.
     */
    primaryColor: z.string().optional(),
    /**
     * Background color for the header.
     */
    headerBackgroundColor: z.string().optional(),
    /**
     * Color for the header links.
     */
    headerLinksColor: z.string().optional(),
});
export type ContentThemeColors = z.infer<typeof ContentThemeColorsSchema>;

const ContentThemeLogosSchema = z.object({
    /**
     * Custom header logo url for the theme mode.
     */
    headerLogoURL: z.string().optional(),
    /**
     * Custom footer logo url for the theme mode.
     */
    footerLogoURL: z.string().optional(),
});
export type ContentThemeLogos = z.infer<typeof ContentThemeLogosSchema>;

const ContentThemeCustomizationsSchema = ContentThemeColorsSchema.and(ContentThemeLogosSchema);
export type ContentThemeCustomizations = z.infer<typeof ContentThemeCustomizationsSchema>;

const ThemeCustomizationsSchema = z.object({
    light: ContentThemeCustomizationsSchema.optional(),
    dark: ContentThemeCustomizationsSchema.optional(),
});
export type ThemeCustomizations = z.infer<typeof ThemeCustomizationsSchema>;

/**
 * Design theme for the content.
 */
export const ContentHeaderThemeSchema = z.enum([
    'none', // No header
    'bold', // Primary color header
    'contrast', // Contrast header
    'matching', // Matching header
]);
export type ContentHeaderTheme = z.infer<typeof ContentHeaderThemeSchema>;

/**
 * Content header theme resolved against a theme mode.
 */
export const ALL_RESOLVED_CONTENT_HEADER_THEMES = z.enum(['none', 'bold', 'light', 'dark']);
export type ResolvedContentHeaderTheme = z.infer<typeof ALL_RESOLVED_CONTENT_HEADER_THEMES>;

/**
 * Base type for content links
 */
const ContentLinkSchema = z.object({
    title: z.string(),
    to: ContentReferenceSchema,
});
export type ContentLink = z.infer<typeof ContentLinkSchema>;

const ContentLinksGroupSchema = z.object({
    title: z.string(),
    links: z.array(ContentLinkSchema),
});
export type ContentLinksGroup = z.infer<typeof ContentLinksGroupSchema>;

/**
 * Entry in the header.
 */
const ContentHeaderItemSchema = ContentLinkSchema.extend({
    to: ContentReferenceSchema.nullable(),
    style: z.enum(['link', 'button-primary', 'button-secondary']).optional(),
    links: z.array(ContentLinkSchema).optional(),
});

/**
 * Entry in the footer
 */
export type ContentFooterLink = ContentLinksGroup;

/**
 * Global content footer.
 */

const ContentFooterSchema = z.object({
    /**
     * @see ContentThemeLogos
     * @deprecated
     */
    logoURL: z.string().optional(),
    linkGroups: z.array(ContentLinksGroupSchema).optional(),
    copyright: z.string().optional(),
});
export type ContentFooter = z.infer<typeof ContentFooterSchema>;

/**
 * Language for the UI.
 */
export const ContentLanguageSchema = z.enum(api.CustomizationLocale);
export type ContentLanguage = z.infer<typeof ContentLanguageSchema>;

/**
 * Font for the UI
 */
export const ContentFontSchema = z.enum(api.CustomizationDefaultFont);
export type ContentFont = z.infer<typeof ContentFontSchema>;

export const ALL_CONTENT_FONTS = arrayOfAll<api.CustomizationDefaultFont>()([
    api.CustomizationDefaultFont.ABCFavorit,
    api.CustomizationDefaultFont.Inter,
    api.CustomizationDefaultFont.Roboto,
    api.CustomizationDefaultFont.RobotoSlab,
    api.CustomizationDefaultFont.OpenSans,
    api.CustomizationDefaultFont.SourceSansPro,
    api.CustomizationDefaultFont.Lato,
    api.CustomizationDefaultFont.Ubuntu,
    api.CustomizationDefaultFont.Raleway,
    api.CustomizationDefaultFont.Merriweather,
    api.CustomizationDefaultFont.Overpass,
    api.CustomizationDefaultFont.NotoSans,
    api.CustomizationDefaultFont.IBMPlexSerif,
    api.CustomizationDefaultFont.Poppins,
    api.CustomizationDefaultFont.FiraSans,
]);

export const ALL_MONOSPACE_FONTS = arrayOfAll<api.CustomizationDefaultMonospaceFont>()([
    api.CustomizationDefaultMonospaceFont.IBMPlexMono,
    api.CustomizationDefaultMonospaceFont.FiraCode,
    api.CustomizationDefaultMonospaceFont.JetBrainsMono,
    api.CustomizationDefaultMonospaceFont.SourceCodePro,
    api.CustomizationDefaultMonospaceFont.RobotoMono,
    api.CustomizationDefaultMonospaceFont.SpaceMono,
    api.CustomizationDefaultMonospaceFont.DMMono,
    api.CustomizationDefaultMonospaceFont.Inconsolata,
]);

export const FREE_FONTS = [
    api.CustomizationDefaultFont.ABCFavorit,
    api.CustomizationDefaultFont.Inter,
    api.CustomizationDefaultMonospaceFont.IBMPlexMono,
];

export const THEME_PRESETS: Array<{
    theme: api.CustomizationHeaderPreset;
    label: string;
    needAdvancedCustomization: boolean;
}> = [
    {
        theme: api.CustomizationHeaderPreset.Default,
        label: 'Default',
        needAdvancedCustomization: false,
    },
    {
        theme: api.CustomizationHeaderPreset.Bold,
        label: 'Bold',
        needAdvancedCustomization: true,
    },
    {
        theme: api.CustomizationHeaderPreset.Contrast,
        label: 'Contrast',
        needAdvancedCustomization: true,
    },
    {
        theme: api.CustomizationHeaderPreset.Custom,
        label: 'Custom',
        needAdvancedCustomization: true,
    },
];

/**
 * Pre-set theme for the content. Comes with a set of colors and fonts.
 */
export const ContentThemePresetSchema = z.enum(['default', 'bold', 'contrast', 'custom']);
export type ContentThemePreset = z.infer<typeof ContentThemePresetSchema>;

/**
 * Theme mode - light, dark (based on browser settings).
 */
export const ContentThemeModeSchema = z.enum(api.CustomizationThemeMode);
export type ContentThemeMode = z.infer<typeof ContentThemeModeSchema>;

/**
 * Theme corner.
 */
export const ContentThemeCornersSchema = z.enum(['straight', 'rounded']);
export type ContentThemeCorners = z.infer<typeof ContentThemeCornersSchema>;

/**
 * Theme background.
 */
export const ContentThemeBackgroundSchema = z.enum(['plain', 'match']);
export type ContentThemeBackground = z.infer<typeof ContentThemeBackgroundSchema>;

/**
 * Deafult theme mode for content, light mode.
 */
export const THEME_MODE_DEFAULT = api.CustomizationThemeMode.Light;

/**
 * Deafult theme corners for content, rounded.
 */
export const THEME_CORNER_DEFAULT = 'rounded';

const ContentThemeSchema = z.object({
    color: ContentThemeModeSchema,
    radius: ContentThemeCornersSchema,
});
export type ContentTheme = z.infer<typeof ContentThemeSchema>;

/**
 * Level of access for a team or user.
 */
export { MemberRole as ContentPermissionLevel };
export const ContentPermissionLevelOrNoneSchema = z.union([z.enum(MemberRole), z.null()]);
export type ContentPermissionLevelOrNone = MemberRoleOrGuest;

/**
 * Default permission for a piece of content.
 */
export const DefaultPermissionLevelSchema = z.union([
    ContentPermissionLevelOrNoneSchema,
    z.literal('inherit'),
]);
export type DefaultPermissionLevel = z.infer<typeof DefaultPermissionLevelSchema>;

export const URLSchema = z.url().max(2048);

/**
 * Configuration for sharing collections or spaces.
 */
export const ContentSharingSchema = ContentOwnershipSchema.extend({
    /**
     * Should this content inherit customization from its parent.
     * In that case customization settings below will be ignored during rendering if the parent is public.
     *
     * This settings only applies to space.
     */
    inheritCustomization: z.boolean().optional(),
    /**
     * Permission for people who can access the parent (org or collection).
     * null means that the content cannot be accessed
     */
    defaultPermission: DefaultPermissionLevelSchema,
    /**
     * Is this content published as a public url
     * @deprecated Should not be used anymore. Use `resolveSpacePublishedInfo` instead.
     */
    visibility: z.enum(api.ContentVisibility),
    /**
     * Language for the UI.
     */
    language: ContentLanguageSchema.optional(),
    /**
     * Font in the UI
     */
    font: ContentFontSchema.optional(),
    /**
     * Theme preset of the content (matching, bold, contrast, custom, etc)
     */
    themePreset: ContentThemePresetSchema.optional(),
    /**
     * Default theme mode of the public content (light, dark)
     */
    themeMode: ContentThemeModeSchema.optional(),
    /**
     * Allow published content reader to toggle between theme mode (dark/light)
     * @default false
     */
    themeModeTogglable: z.boolean().optional(),
    /**
     * Customizations preferences for each theme mode
     */
    themeCustomizations: ThemeCustomizationsSchema.optional(),
    /**
     * Theme for corners.
     */
    themeCorners: ContentThemeCornersSchema.optional(),
    /**
     * Theme for background.
     */
    themeBackground: ContentThemeBackgroundSchema.optional(),
    /**
     * Primary color for text.
     * @dreprecated
     */
    primaryColor: z.string().optional(),
    /**
     * Theme for the header.
     * @deprecated
     */
    headerTheme: ContentHeaderThemeSchema.optional(),
    /**
     * Entries in the header (when theme is not "none")
     */
    headerLinks: z.array(ContentHeaderItemSchema).optional(),
    /**
     * Global footer
     */
    footer: ContentFooterSchema.optional(),
    /**
     * If defined, the custom icon URL for this content when published.
     */
    publishedIconURL: z.string().optional(),
    /**
     * If defined, the emoji for this content published.
     */
    publishedEmoji: z.string().optional(),
    /**
     * URL to a logo replacing the icon & title
     * @deprecated
     */
    logoURL: URLSchema.optional(),
    /**
     * URL for the social preview image.
     */
    socialPreviewURL: URLSchema.optional(),
    /**
     * Google Analytics Integration.
     */
    googleAnalytics: ContentGoogleAnalyticsConfigSchema.optional(),
    /**
     * Intercom Integration.
     */
    intercom: z
        .object({
            enabled: z.boolean().optional(),
            appID: z.string().optional(),
        })
        .optional(),
    /**
     * Hostname for the public content.
     */
    customHostname: z.string().optional(),
    /**
     * Path in the hostname.
     * @deprecated
     */
    path: z.string().optional(),
    /**
     * PDF export enabled for public content.
     */
    hasPDFExport: z.boolean().optional(),
    /**
     * Prompt public readers to rate content.
     */
    hasPublicFeedback: z.boolean().optional(),
    /**
     * If true, the space is configured to be indexed in the vector search engine.
     * And the AI search can be shown in the published content UI.
     *
     * The content of the space can also be indexed in the vector database in the case where the owning organization.hasAI = true.
     */
    hasAISearch: z.boolean().optional(),
    /**
     * The title of the content when published (can be different from the title
     * of the content in the app for SEO or organisation purposes).
     */
    publishedTitle: z.string().optional(),
    /**
     * Whether the GitBook trademark ("Powered by GitBook") should be hidden
     * in the public content (can only be changed by a super-admin)
     */
    hideTrademark: z.boolean().optional(),
    /**
     * Whether the public content should show a link to edit the content on the
     * git provider set up in the GitSync
     */
    showEditOnGitProvider: z.boolean().optional(),
    /**
     * Custom link to a privacy policy.
     */
    privacyPolicyURL: URLSchema.optional(),
    /**
     * Whether the pagination navigation should be displayed on pages.
     * @default true
     */
    showPagination: z.boolean().optional(),
    /**
     * Whether the content was created by the migration script.
     * Denotes the ID of the user from which the content was migrated.
     */
    migrated: z.string().optional(),
});
export type ContentSharing = z.infer<typeof ContentSharingSchema>;

/**
 * List of all valid ContentPermissionLevel.
 *
 * The list must be kept in order of importance i.e. the first entry must have all the permissions of the entries below it.
 */
export const ALL_CONTENT_PERMISSIONS: ContentPermissionLevelOrNone[] = [
    MemberRole.Admin,
    MemberRole.Create,
    MemberRole.Review,
    MemberRole.Edit,
    MemberRole.Comment,
    MemberRole.Read,
    null,
];

export const ContentPermissionsSchema = z.object({
    level: ContentPermissionLevelOrNoneSchema,
    /**
     * The user who created this permission.
     */
    createdBy: z.string(),
    /**
     * The user who deleted this permission, if it's deleted or being deleted.
     */
    deletedBy: z.string().optional(),
});
export type ContentPermissions = z.infer<typeof ContentPermissionsSchema>;

/**
 * Permissions for a team in a content.
 */
export const TeamContentPermissionsSchema = ContentPermissionsSchema.extend({
    organization: z.string(),
    team: z.string(),
});
export type TeamContentPermissions = z.infer<typeof TeamContentPermissionsSchema>;

/**
 * Permissions for a user in a content.
 */
export const UserContentPermissionsSchema = ContentPermissionsSchema.extend({
    user: z.string(),
    /**
     * The invite link used to grant this user access, if needed.
     */
    invite: z.string().optional(),
});
export type UserContentPermissions = z.infer<typeof UserContentPermissionsSchema>;

/**
 * Compare two ownerships. Return true if equals.
 */
export function compareContentOwners(
    a: ContentOwnership,
    b: ContentOwnership,
    shouldCompareCollections = false
): boolean {
    if (a.organization !== b.organization) {
        return false;
    }

    if (shouldCompareCollections && a.collection !== b.collection) {
        return false;
    }

    return true;
}

/**
 * Returns whether the content is a top content from a specific owner
 */
export function isTopContentFromOwner(
    owner: ContentOwnership,
    item: Space | Collection,
    options: {
        includeOrphans?: boolean;
    } = {}
) {
    // Ensure we are considering only content that belongs to this user/organization
    if (!compareContentOwners(owner, item)) {
        return false;
    }

    // If filtering with a collection, we only return items in this collection
    if (owner.collection) {
        return item.collection === owner.collection;
    }

    // Otherwise we return top level items, unless we explicitly want to include orphans
    return options.includeOrphans ? true : !item.collection;
}
